import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useFact } from "@/lib/factoryI18n";
import { ArrowLeft, MapPin, QrCode } from "lucide-react";
import PieceLifecycleCard from "@/pages/factory/PieceLifecycleCard";

const CHECKLISTS = {
  "Post-Weave": [
    "Design matches graph",
    "Knot/tuft density within ±5% of spec",
    "Pile height within ±1mm at 9 points",
    "Dimensions within ±1%",
    "Selvage & edges secure",
    "No missing/loose knots, holes, or patches",
    "Back inspection clean",
  ],
  "Post-Wash": [
    "Wash pH 6.5–8.0",
    "Moisture <15%",
    "No colour bleeding / shade change",
    "Pile direction uniform after wash",
    "No shrinkage beyond ±1%",
    "Fringe intact",
  ],
  "Final QC": [
    "Face inspection under D65 light",
    "Dimensions final check ±1%",
    "Corners 90°, lies flat",
    "Colour matches approved sample",
    "Labels correct",
    "Defect count within AQL",
  ],
};

export default function FactoryPieceDetail() {
  const { pieceId } = useParams();
  const { user } = useAuth();
  const { lang } = useLang();
  const f = useFact(lang);
  const [piece, setPiece] = useState(null);
  const [loc, setLoc] = useState("");
  const [gate, setGate] = useState("Post-Weave");
  const [checks, setChecks] = useState({});
  const [defects, setDefects] = useState([]);
  const [gateMsg, setGateMsg] = useState("");
  const canSign = ["prod_qc_coordinator", "coordinator", "director"].includes(user?.role);
  const canInspect = ["qc_officer", "prod_qc_coordinator", "director"].includes(user?.role);

  const load = () => api.get(`/factory/pieces/${pieceId}`).then((r) => setPiece(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [pieceId]);

  const submitGate = async () => {
    setGateMsg("");
    try {
      await api.post("/factory/gates", { piece_id: pieceId, gate, checklist: checks, defects, result: "Pass" });
      setGateMsg("Gate submitted. Awaiting counter-sign.");
      setChecks({}); setDefects([]);
      load();
    } catch (e) {
      setGateMsg("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const countersign = async (gateId) => {
    await api.post(`/factory/gates/${gateId}/counter-sign`, {});
    load();
  };

  const updateLoc = async () => {
    if (!loc.trim()) return;
    await api.patch(`/factory/pieces/${pieceId}/location`, { location: loc.trim() });
    setLoc("");
    load();
  };

  if (!piece) return <div className="text-sm text-[#7C8E9E]">Loading…</div>;

  return (
    <div className="max-w-4xl" data-testid="piece-detail">
      <Link to="/factory/wip" className="text-xs text-[#7C8E9E] hover:text-[#1B3A5C] inline-flex items-center gap-1 mb-3"><ArrowLeft size={12} /> {f("back")}</Link>
      <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xl font-mono font-bold" data-testid="piece-code">{piece.code}</div>
            <div className="text-sm text-[#4A627A]">{piece.design_code} · {piece.size_text} · {piece.colour}</div>
          </div>
          <div className="text-right">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-semibold" data-testid="piece-stage" style={{ background: "var(--brand-navy)", color: "white" }}>{piece.current_stage}</div>
            <div className="text-xs mt-1 text-[#7C8E9E]"><MapPin size={10} className="inline mr-1" />{piece.location}</div>
          </div>
        </div>
        <div className="mt-3 flex gap-2 items-end flex-wrap">
          <input data-testid="piece-loc-input" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="Update location" className="h-9 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          <button data-testid="piece-loc-save" onClick={updateLoc} className="h-9 px-3 text-xs font-semibold rounded-md" style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}>{f("update_location")}</button>
          <Link to={`/factory/qr-labels?ids=${pieceId}`} target="_blank" rel="noopener"
            data-testid="piece-qr-label-btn"
            className="h-9 px-3 text-xs font-semibold rounded-md border border-[#1B3A5C]/15 inline-flex items-center gap-1.5 hover:bg-[#F2ECE0]"
            style={{ color: "var(--brand-navy)" }}>
            <QrCode size={13} /> A4 label
          </Link>
          <Link to={`/factory/qr-labels?ids=${pieceId}&format=thermal&width=80`} target="_blank" rel="noopener"
            data-testid="piece-qr-thermal-btn"
            className="h-9 px-3 text-xs font-semibold rounded-md inline-flex items-center gap-1.5 hover:opacity-90"
            style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}>
            <QrCode size={13} /> Thermal 80mm
          </Link>
        </div>
      </div>

      {canInspect && (
        <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 mt-4" data-testid="gate-form">
          <h2 className="font-bold mb-3" style={{ color: "var(--brand-navy)" }}>{f("gate_inspection")}</h2>
          <div className="flex gap-2 mb-3">
            {["Post-Weave", "Post-Wash", "Final QC"].map((g) => (
              <button key={g} data-testid={`gate-pick-${g}`} onClick={() => setGate(g)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${gate === g ? "text-white" : "text-[#1B3A5C] border border-[#1B3A5C]/15"}`} style={{ background: gate === g ? "var(--brand-navy)" : "transparent" }}>{g}</button>
            ))}
          </div>
          <div className="space-y-1 mb-3">
            {(CHECKLISTS[gate] || []).map((c, i) => (
              <label key={i} className="flex items-center gap-2 text-sm">
                <input data-testid={`gate-check-${i}`} type="checkbox" checked={!!checks[c]} onChange={(e) => setChecks({ ...checks, [c]: e.target.checked })} />
                {c}
              </label>
            ))}
          </div>
          <div className="mb-3">
            <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E] mb-1">Defects</div>
            {defects.map((d, i) => (
              <div key={i} className="flex gap-2 items-center mb-1">
                <select data-testid={`defect-class-${i}`} value={d.class} onChange={(e) => setDefects(defects.map((x, idx) => idx === i ? { ...x, class: e.target.value } : x))} className="h-8 px-2 border border-[#1B3A5C]/15 rounded text-xs">
                  <option>Critical</option><option>Major</option><option>Minor</option>
                </select>
                <input value={d.description} onChange={(e) => setDefects(defects.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} placeholder="Description" className="flex-1 h-8 px-2 border border-[#1B3A5C]/15 rounded text-xs" />
                <button onClick={() => setDefects(defects.filter((_, idx) => idx !== i))} className="text-red-600 text-xs">×</button>
              </div>
            ))}
            <button data-testid="add-defect" onClick={() => setDefects([...defects, { class: "Minor", description: "" }])} className="text-xs text-[#1B3A5C] underline">+ {f("add_defect")}</button>
          </div>
          {defects.some((d) => d.class === "Critical") && (
            <div className="text-xs text-red-700 mb-2">Critical defect → forces Fail. Attach a photo file_id in production usage.</div>
          )}
          <button data-testid="gate-submit" onClick={submitGate} className="px-4 py-2 rounded-full font-semibold text-sm" style={{ background: "var(--brand-gold)", color: "var(--brand-navy)" }}>Submit gate</button>
          {gateMsg && <div className="text-xs mt-2">{gateMsg}</div>}
        </div>
      )}

      <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 mt-4">
        <h2 className="font-bold mb-3" style={{ color: "var(--brand-navy)" }}>Gate history</h2>
        {(piece.gates || []).map((g) => (
          <div key={g.id} className="border-b border-[#1B3A5C]/10 py-2 text-sm" data-testid={`gate-hist-${g.id}`}>
            <div className="flex justify-between">
              <div><b>{g.gate}</b> · {g.result} {g.counter_signed ? "· ✓ signed" : "· pending sign"}</div>
              <div className="text-xs text-[#7C8E9E]">{new Date(g.created_at).toLocaleString()}</div>
            </div>
            {!g.counter_signed && canSign && (
              <button data-testid={`gate-sign-${g.id}`} onClick={() => countersign(g.id)} className="mt-1 px-3 py-1 rounded-full text-xs font-semibold" style={{ background: "var(--brand-navy)", color: "white" }}>{f("counter_sign")}</button>
            )}
            {g.defects?.length > 0 && <div className="text-xs text-[#7C8E9E] mt-1">Defects: {g.defects.map((d) => `${d.class}: ${d.description}`).join(" · ")}</div>}
          </div>
        ))}
        {(piece.gates || []).length === 0 && <div className="text-xs text-[#7C8E9E]">No gate records yet.</div>}
      </div>

      {(piece.finishing || []).length > 0 && (
        <div className="bg-white border border-[#1B3A5C]/10 rounded-lg p-4 mt-4">
          <h2 className="font-bold mb-3" style={{ color: "var(--brand-navy)" }}>{f("finishing")}</h2>
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E]"><th className="text-left py-1">Op</th><th className="text-left">Name</th><th className="text-left">Status</th></tr></thead>
            <tbody>
              {piece.finishing.sort((a, b) => a.op - b.op).map((r) => (
                <tr key={r.id} className="border-t border-[#1B3A5C]/5" data-testid={`fin-row-${r.op}`}><td className="py-1">{r.op}</td><td>{r.name}</td><td>{r.status}{r.status === "N/A" && r.na_reason ? ` — ${r.na_reason}` : ""}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PieceLifecycleCard pieceId={pieceId} />
    </div>
  );
}
